import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import SearchBar from '@/components/submission/SearchBar';
import SubmissionForm from '@/components/submission/SubmissionForm';
import ScoringResult from '@/components/submission/ScoringResult';
import ClientRiskNotes from '@/components/ClientRiskNotes';
import EntityEnrichmentCard from '@/components/submission/EntityEnrichmentCard';
import RCDPanel from '@/components/submission/RCDPanel';
import SubmissionSteps from '@/components/submission/SubmissionSteps';
import CreateBusinessCard from '@/components/submission/CreateBusinessCard';
import { completeFirstRunStep } from '@/lib/firstRun';
import { Search, Clock, Pencil } from 'lucide-react';

const EXAMPLE_SEARCHES = ['Fletcher', 'Construction', 'Transport', 'Engineering'];

const INITIAL_FORM = {
  annual_turnover: '', employee_count: '', sum_insured: '',
  construction_type: 'brick_veneer', public_access: false,
  claims_count: 0, claims_total_value: 0, prior_cancellation_3yr: false,
  overseas_operations: false, whs_system: false, risk_management: false,
  director_disqualification: false, prior_insolvency_2yr: false,
  combustible_cladding: 'none', notes: '', covers_selected: [],
  acn: '', directors: [], street_address: '', suburb: '', state: '', postcode: '',
};

export default function EntitySearchPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [insuranceClasses, setInsuranceClasses] = useState({});
  useEffect(() => { api.get('/insurance-classes').then(r => setInsuranceClasses(r.data)).catch(() => {}); }, [api]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scoringResult, setScoringResult] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [clientRef, setClientRef] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftStatus, setDraftStatus] = useState('');
  const [locationRisk, setLocationRisk] = useState(null);
  const [dupInfo, setDupInfo] = useState(null);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recentEntitySearches') || '[]'); } catch { return []; }
  });

  const saveRecent = (entity) => {
    try {
      const prev = JSON.parse(localStorage.getItem('recentEntitySearches') || '[]').filter(r => r.entity_id !== entity.entity_id);
      const next = [{ entity_id: entity.entity_id, name: entity.legal_name, abn: entity.abn || entity.nzbn }, ...prev].slice(0, 5);
      localStorage.setItem('recentEntitySearches', JSON.stringify(next));
      setRecentSearches(next);
    } catch { /* noop */ }
  };

  const entityAddressPrefill = (entity) => ({
    street_address: entity.primary_address || '',
    suburb: (entity.suburb || '').replace(/\s*(AU|NZ)\s*\d{4}$/i, '').trim(),
    state: entity.state || '',
    postcode: entity.postcode || '',
  });

  const checkDuplicates = (entityId) => {
    api.get(`/submissions/duplicate-check?entity_id=${entityId}`)
      .then(r => setDupInfo(r.data.count > 0 ? r.data : null))
      .catch(() => setDupInfo(null));
  };

  const loadDrafts = useCallback(() => {
    api.get('/submission-drafts').then(r => setDrafts(r.data.drafts)).catch(() => {});
  }, [api]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  // Auto-save draft (debounced) whenever the form changes
  useEffect(() => {
    if (!showForm || !selectedEntity || !clientRef || submitting || scoringResult) return;
    if (JSON.stringify(form) === JSON.stringify(INITIAL_FORM)) return;
    setDraftStatus('saving');
    const t = setTimeout(() => {
      api.put(`/submission-drafts/${clientRef}`, {
        entity_id: selectedEntity.entity_id,
        entity_name: selectedEntity.legal_name,
        abn: selectedEntity.abn,
        form,
      }).then(() => setDraftStatus(`Draft saved ${new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`))
        .catch(() => setDraftStatus(''));
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, showForm, clientRef]);

  const resumeDraft = async (d) => {
    try {
      const { data: entity } = await api.get(`/entities/${d.entity_id}`);
      setSelectedEntity(entity);
      setForm({ ...INITIAL_FORM, ...entityAddressPrefill(entity), ...(d.form || {}) });
      setClientRef(d.draft_id);
      setShowForm(true);
      setScoringResult(null);
      checkDuplicates(d.entity_id);
      setDraftStatus(`Draft saved ${new Date(d.updated_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`);
    } catch (err) { console.error('Resume draft failed:', err?.message); }
  };

  const discardDraft = async (draftId) => {
    await api.delete(`/submission-drafts/${draftId}`).catch(() => {});
    setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
  };

  const newRef = () => `SUB-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 5).toUpperCase()}`;

  const searchEntities = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/entities/search?q=${encodeURIComponent(q)}&limit=10`);
      setResults(data);
      if (data.length) completeFirstRunStep(api, 'find');
    setSearching(false);
    } catch (err) { console.error("API error:", err?.message); }
  }, [api]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(window._searchTimeout);
    window._searchTimeout = setTimeout(() => searchEntities(val), 300);
  };

  const selectEntity = (entity) => {
    saveRecent(entity);
    setSelectedEntity(entity);
    setShowForm(true);
    setScoringResult(null);
    setResults([]);
    setQuery('');
    setClientRef(newRef());
    setForm({ ...INITIAL_FORM, ...entityAddressPrefill(entity) });
    checkDuplicates(entity.entity_id);
  };

  // Quick-quote deep link: /new-business?tab=submit&entity_id=X preselects the client
  const [searchParams] = useSearchParams();
  const prefillId = searchParams.get('entity_id');
  useEffect(() => {
    if (!prefillId || selectedEntity) return;
    api.get(`/entities/${prefillId}`)
      .then(r => { setSelectedEntity(r.data); setShowForm(true); setClientRef(newRef()); setForm({ ...INITIAL_FORM, ...entityAddressPrefill(r.data) }); checkDuplicates(prefillId); })
      .catch(err => console.error('Prefill entity load failed:', err?.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.turnover_by_state) {
      const tot = Object.values(form.turnover_by_state).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      if (Math.abs(tot - 100) > 0.01) return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/submissions', {
        entity_id: selectedEntity.entity_id,
        client_ref: clientRef,
        business_name: selectedEntity.legal_name,
        abn: selectedEntity.abn,
        nzbn: selectedEntity.nzbn,
        industry_description: selectedEntity.industry_code,
        annual_turnover: parseFloat(form.annual_turnover) || 0,
        employee_count: parseInt(form.employee_count) || 0,
        sum_insured: parseFloat(form.sum_insured) || 0,
        excess: form.excess != null ? parseFloat(form.excess) : null,
        construction_type: form.construction_type,
        public_access: form.public_access,
        claims_count: parseInt(form.claims_count) || 0,
        claims_total_value: parseFloat(form.claims_total_value) || 0,
        prior_cancellation_3yr: form.prior_cancellation_3yr,
        overseas_operations: form.overseas_operations,
        whs_system: form.whs_system,
        risk_management: form.risk_management,
        director_disqualification: form.director_disqualification,
        prior_insolvency_2yr: form.prior_insolvency_2yr,
        combustible_cladding: form.combustible_cladding,
        covers_selected: form.covers_selected,
        notes: form.notes,
        acn: form.acn || null,
        directors: form.directors || [],
        street_address: form.street_address || '',
        suburb: form.suburb || '',
        state: form.state || '',
        postcode: form.postcode || '',
        location_risk: locationRisk,
        turnover_by_state: form.turnover_by_state || undefined,
      });
      setScoringResult(data);
      completeFirstRunStep(api, 'submit');
      setDrafts(prev => prev.filter(d => d.draft_id !== clientRef));
      setDraftStatus('');
    setSubmitting(false);
    } catch (err) { console.error("API error:", err?.message); }
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleCover = (cover) => {
    setForm(prev => ({
      ...prev,
      covers_selected: prev.covers_selected.includes(cover)
        ? prev.covers_selected.filter(c => c !== cover)
        : [...prev.covers_selected, cover]
    }));
  };

  const reset = () => {
    setSelectedEntity(null);
    setShowForm(false);
    setScoringResult(null);
    setForm(INITIAL_FORM);
    setDraftStatus('');
    setLocationRisk(null);
    setDupInfo(null);
    loadDrafts();
  };

  // Scoring Result view
  if (scoringResult) {
    return (
      <div className="space-y-4">
        <SubmissionSteps done={[true, true, true, true]} current={-1} />
        <ScoringResult result={scoringResult} onReset={reset} onUpdated={setScoringResult} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="entity-search-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans', sans-serif" }}>
          {showForm ? 'New Submission' : 'Entity Search'}
        </h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>
          {showForm ? `Submitting for: ${selectedEntity?.legal_name}` : 'Search by ABN, NZBN, or business name'}
        </p>
      </div>

      <SubmissionSteps
        done={submitting ? [true, true, true, false] : showForm ? [true, true, false, false] : [false, false, false, false]}
        current={submitting ? 3 : showForm ? 2 : 0}
      />

      {/* Search */}
      {!showForm && (
        <>
          <SearchBar query={query} onSearch={handleSearch} searching={searching} results={results} onSelect={selectEntity}
            onEdit={(entity) => navigate(`/client/${entity.entity_id}`)} />
          <CreateBusinessCard prefillName={query} onCreated={selectEntity} />
          {!query && results.length === 0 && (
            <div className="rounded-sm border bg-white p-4 space-y-3" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="search-empty-state">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#5C6B7A' }}>
                  <Search className="w-3 h-3" /> Try an example search
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLE_SEARCHES.map(ex => (
                    <button key={ex} onClick={() => { setQuery(ex); searchEntities(ex); }} data-testid={`example-search-${ex.toLowerCase()}`}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors hover:bg-[#E8F0FE]"
                      style={{ color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.25)' }}>
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
              {recentSearches.length > 0 && (
                <div data-testid="recent-searches">
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#5C6B7A' }}>
                    <Clock className="w-3 h-3" /> Recent searches
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map(r => (
                      <button key={r.entity_id} data-testid={`recent-search-${r.entity_id}`}
                        onClick={() => api.get(`/entities/${r.entity_id}`).then(res => selectEntity(res.data)).catch(() => { setQuery(r.name); searchEntities(r.name); })}
                        className="px-3 py-1.5 rounded-full text-xs transition-colors hover:bg-[#F5F7FA]"
                        style={{ color: '#0D1B2A', border: '1px solid rgba(136,150,168,0.3)' }}>
                        {r.name}{r.abn ? <span className="ml-1.5 font-mono text-[10px]" style={{ color: '#64748B' }}>{r.abn}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {drafts.length > 0 && (
            <div className="rounded-sm border bg-white p-4" style={{ borderColor: 'rgba(232,160,32,0.3)' }} data-testid="drafts-list">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#B27516' }}>
                Resume a draft ({drafts.length})
              </p>
              {drafts.map(d => (
                <div key={d.draft_id} className="flex items-center gap-3 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`draft-row-${d.draft_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: '#0D1B2A' }}>{d.entity_name || d.entity_id}</p>
                    <p className="text-[10px] font-mono" style={{ color: '#5C6B7A' }}>{d.draft_id} · saved {new Date(d.updated_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <button onClick={() => resumeDraft(d)} data-testid={`draft-resume-${d.draft_id}`}
                    className="px-3 py-1 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90" style={{ background: '#1A4C7C' }}>Resume</button>
                  <button onClick={() => discardDraft(d.draft_id)} data-testid={`draft-delete-${d.draft_id}`}
                    className="px-2 py-1 rounded-sm text-[10px] font-medium transition-colors hover:bg-red-50" style={{ color: '#E24B4A' }}>Discard</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Submission Form */}
      {showForm && !scoringResult && !submitting && (
        <>
          <div className="rounded-sm border bg-white p-3 flex flex-wrap items-center gap-3" style={{ borderColor: 'rgba(26,76,124,0.2)' }} data-testid="class-picker-card">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Insurance class</span>
            <select data-testid="class-picker-select" value="sme_commercial"
              onChange={e => { if (e.target.value !== 'sme_commercial') navigate(`/new-business?tab=quote&entity_id=${selectedEntity.entity_id}&class_id=${e.target.value}`); }}
              className="px-2.5 py-1.5 text-xs rounded-sm border focus:outline-none" style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }}>
              <option value="sme_commercial">SME Commercial Package (this form)</option>
              {Object.entries(insuranceClasses).filter(([id]) => id !== 'sme_commercial').map(([id, c]) => (
                <option key={id} value={id}>{c.name || id}</option>
              ))}
            </select>
            <span className="text-[11px]" style={{ color: '#64748B' }}>Picking another class opens Quote &amp; Bind with this business preselected</span>
            <button onClick={() => navigate(`/client/${selectedEntity.entity_id}`)} data-testid="edit-business-link"
              className="ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-semibold transition-colors hover:bg-[rgba(26,76,124,0.08)]"
              style={{ color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.3)' }}>
              <Pencil className="w-3 h-3" /> Edit business details
            </button>
          </div>
          <EntityEnrichmentCard entity={selectedEntity} />
          {!selectedEntity.virtual && <RCDPanel entityId={selectedEntity.entity_id} />}
          <ClientRiskNotes entityId={selectedEntity.entity_id} editable={false} compact />
          <SubmissionForm
          entity={selectedEntity}
          clientRef={clientRef}
          draftStatus={draftStatus}
          dupInfo={dupInfo}
          form={form}
          updateForm={updateForm}
          toggleCover={toggleCover}
          onSubmit={handleSubmit}
          onCancel={reset}
          submitting={submitting}
          onLocationRisk={setLocationRisk}
        />
        </>
      )}

      {/* Scoring in progress */}
      {submitting && <ScoringProgress clientRef={clientRef} businessName={selectedEntity?.legal_name} />}
    </div>
  );
}

const SCORING_STEPS = [
  'Resolving entity & golden record',
  'Running 21-dimension AIRA risk scoring',
  'Checking override rules & underwriter notes',
  'Rating premium via MARAIS engine',
  'Generating AI risk narrative',
];

function ScoringProgress({ clientRef, businessName }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => Math.min(s + 1, SCORING_STEPS.length - 1)), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="max-w-xl mx-auto p-8 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="scoring-progress">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#00B4D8', borderTopColor: 'transparent' }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>AIRA is scoring {businessName}</p>
          <p className="text-[11px] font-mono" style={{ color: '#5C6B7A' }}>{clientRef}</p>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {SCORING_STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 text-xs" style={{ color: i < step ? '#1D9E75' : i === step ? '#0D1B2A' : '#64748B' }}>
            <span className="w-4 text-center">{i < step ? '✓' : i === step ? '●' : '○'}</span>{s}
          </div>
        ))}
      </div>
      <div className="mt-4 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${((step + 1) / SCORING_STEPS.length) * 100}%`, background: '#00B4D8' }} />
      </div>
    </div>
  );
}
